import React, { useContext, useMemo } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer } from 'recharts';
import { DataAndFilterContext } from '../../context/DataAndFilterContext';

const PIE_COLORS = ['#FF7F50', '#87CEEB', '#9370DB', '#FFD700', '#FF6347', '#00C9A7', '#9B59B6', '#2ECC71', '#E67E22'];

export default function PreviousSolutionAndTriggerPies() {
  const { filteredData } = useContext(DataAndFilterContext);

  const previousSolutionData = useMemo(() => {
    const categoryCount = {};
    const valid = (filteredData || []).filter(s => s.previous_solution || s.previous_solution_category);
    valid.forEach((item) => {
      const category = item.previous_solution_category;
      if (category) categoryCount[category] = (categoryCount[category] || 0) + 1;
    });
    const total = valid.length || 1;
    return Object.keys(categoryCount)
      .map((name) => ({
        name,
        value: categoryCount[name],
        percentage: ((categoryCount[name] / total) * 100).toFixed(2),
      }))
      .sort((a, b) => b.value - a.value);
  }, [filteredData]);

  const triggerPieData = useMemo(() => {
    const counts = new Map();
    const bump = (title) => {
      const t = (title || '').toString().trim();
      if (!t) return;
      counts.set(t, (counts.get(t) || 0) + 1);
    };
    for (const s of filteredData || []) {
      if (s.trigger_category) bump(s.trigger_category);
      const arrays = [s.triggers, s.switching_triggers, s.why_customers_switch].filter(Boolean);
      for (const arr of arrays) {
        for (const t of arr) bump(t?.trigger_category || t?.title || t?.reason || t?.text);
      }
    }
    const total = Array.from(counts.values()).reduce((a, c) => a + c, 0) || 1;
    return Array.from(counts.entries())
      .map(([name, value]) => ({
        name, value, percentage: ((value / total) * 100).toFixed(2),
      }))
      .sort((a, b) => b.value - a.value);
  }, [filteredData]);

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={6}>
        <Typography variant="h6">Previous Solutions</Typography>
        <Typography variant="body2" gutterBottom>
          These are the previous solutions customers mentioned using.
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={previousSolutionData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name }) => name}>
              {previousSolutionData.map((entry, index) => (
                <Cell key={`prev-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
              ))}
            </Pie>
            <RTooltip formatter={(value, name, entry) => [`${value} (${entry.payload.percentage}%)`, 'Files']} />
          </PieChart>
        </ResponsiveContainer>
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="h6">Switching Triggers</Typography>
        <Typography variant="body2" gutterBottom>
          The most common triggers customers cited before switching.
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={triggerPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name }) => name}>
              {triggerPieData.map((entry, index) => (
                <Cell key={`trig-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
              ))}
            </Pie>
            <RTooltip formatter={(value, name, entry) => [`${value} (${entry.payload.percentage}%)`, 'Occurrences']} />
          </PieChart>
        </ResponsiveContainer>
      </Grid>
    </Grid>
  );
}
